import React, { useEffect } from "react";
import { AudienceHeader } from "./AudienceChrome";
import SEO from "./SEO";
import "./PrivacyPolicy.css";

/**
 * Privacy Policy.
 *
 * Content is the 2026 revision, which covers both sides of the product: legal
 * professionals using the workspace, consumers using the free legal-question
 * tools (including anonymous, no-account use), and the attorney/paralegal
 * directory. Keep this in step with the source document rather than editing
 * prose here piecemeal — the numbering is referenced elsewhere.
 */
export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  return (
    <div className="policy-page">
      <SEO
        title="Privacy Policy"
        path="/privacy-policy"
        description="LawFusion privacy policy — how we collect, use, and protect information from legal professionals, consumers asking legal questions, and directory members."
      />
      <AudienceHeader />
      <main className="policy-wrapper">
        <section className="policy-card">
          <h1>🔒 Privacy Policy</h1>

          <p className="policy-last-updated">
            Effective Date: August 24, 2026 · Last Updated: August 24, 2026
          </p>

          <div className="policy-content">
            <section className="policy-section">
              <h2>Introduction</h2>
              <p>
                LawFusion.AI ("LawFusion," "we," "our," or "us") is owned and operated by <strong>Comparative AI Solutions LLC</strong>, a California limited liability company ("Company"). We respect your privacy and are committed to protecting your information. This Privacy Policy explains how we collect, use, share, and protect information when you use our website, platform, and services (the "Service"), whether you are a legal professional, a consumer asking a legal question, or a visitor browsing our attorney and paralegal directory.
              </p>
            </section>

            <section className="policy-section">
              <h2>1. Information We Collect</h2>
              <ul>
                <li>
                  <strong>Account &amp; Billing:</strong> Name, email, firm/company name, role, billing address, and payment method details (processed by our payment processor).
                </li>
                <li>
                  <strong>Usage &amp; Device Data:</strong> IP address, device/browser, timestamps, pages/actions taken, diagnostics, crash logs, and security logs. This includes usage data collected from visitors who use portions of the Service without creating an account, such as our anonymous consumer legal-question tool. We may use IP address or device information to apply usage limits, rate limits, or other restrictions on anonymous or free access.
                </li>
                <li>
                  <strong>User Content (Inputs/Outputs):</strong> Questions, prompts, documents, files, and AI outputs you submit or receive through the Service.
                  <ul>
                    <li>
                      <em>Professional users:</em> if you choose to upload materials containing confidential or sensitive information (e.g., client PII, PHI, financial account numbers, SSNs, privileged materials, or trade secrets), you do so at your own risk and represent that you have authority to share such materials with third-party AI processors for legal analysis purposes. You assume full responsibility for any such uploads.
                    </li>
                    <li>
                      <em>Consumer users:</em> if you submit a legal question, upload a document (such as a lease, letter, notice, or contract), or provide other details about your situation, that information is processed to generate AI responses and a summary for you. You should not submit information you do not have the legal right to provide. Submitting information to LawFusion does not create attorney-client privilege or any other legal privilege.
                    </li>
                  </ul>
                </li>
                <li>
                  <strong>Directory Listing Information:</strong> If you are an attorney, paralegal, or other legal professional who creates a directory listing, we collect and publicly display the information you submit, which may include your name, firm name, practice areas, location, contact information (such as email or website), and professional experience. Phone numbers collected for verification or account-related purposes are not publicly displayed unless the directory member affirmatively chooses to display the phone number on their listing. This information is provided by you and is displayed publicly to help consumers and other visitors search the directory.
                </li>
                <li>
                  <strong>Cookies/Analytics:</strong> Essential cookies to operate the Service and optional analytics cookies to understand usage and improve performance.
                </li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>2. How We Use Information</h2>
              <p>
                We use your information to provide, secure, troubleshoot, and improve the Service; generate AI responses and summaries; display directory listings; communicate with you (support, updates, billing); enforce our Terms; prevent fraud/abuse; apply usage or rate limits to anonymous or free access; and comply with applicable laws.
              </p>
            </section>

            <section className="policy-section">
              <h2>3. AI Processing &amp; Third Parties</h2>
              <p>
                We route prompts, questions, and uploaded documents to <strong>third-party AI providers</strong> (such as OpenAI, Anthropic, Google, and xAI) to generate outputs, including individual AI analyses and summaries generated for consumer legal questions. This list is illustrative and may not represent all providers we work with.
              </p>
              <p>
                While our system is designed to help users identify and reduce AI-generated errors and inconsistencies, we <strong>do not control or guarantee the accuracy, completeness, or reliability</strong> of any AI outputs. Third-party providers process information subject to their applicable contractual terms, privacy terms, and any data-processing agreements we maintain with them.
              </p>
              <p>
                LawFusion does not use the contents of consumer legal questions or uploaded legal documents to train LawFusion-owned AI models or to build evaluation datasets. Authorized personnel may review individual submissions on a limited basis where reasonably necessary for customer support, debugging, quality control, or investigating suspected abuse or violations of our Terms of Service. Submitted questions and documents are otherwise processed to generate the AI responses and summary you requested. Third-party AI providers may retain or use submitted content subject to their own terms and any data-processing agreements we maintain with them.
              </p>

              <h3>3A. Cookies &amp; Analytics</h3>
              <p>
                We use essential cookies to operate core features (authentication, security, preferences) and optional analytics cookies to understand usage. You may disable non-essential cookies through browser settings or, where available, a cookie preferences tool.
              </p>

              <h3>3B. Advertising</h3>
              <p>
                We may display sponsored, promoted, or advertising content from attorneys, paralegals, or firms (for example, on our consumer legal-question pages). We do not sell personal information for monetary consideration. If we engage in activity that constitutes "sharing" personal information for cross-context behavioral advertising under applicable state privacy laws, we will disclose that practice and provide an opt-out mechanism, such as a "Do Not Sell or Share My Personal Information" link, as required by law.
              </p>
            </section>

            <section className="policy-section">
              <h2>4. Data Retention &amp; Deletion</h2>
              <p>
                We retain personal information for as long as reasonably necessary to provide the Service, comply with legal, tax, accounting, security, fraud-prevention, and dispute-resolution obligations, enforce our agreements, and protect our legal rights. Retention periods vary depending on the type of information and the purpose for which it is processed.
              </p>
              <p>
                For anonymous or no-login consumer use, we retain submitted questions, documents, and generated outputs only as long as reasonably necessary to provide the Service and comply with legal obligations, unless you create an account to save your history.
              </p>
              <p>
                Directory listing information is retained for as long as the listing remains active, and for a reasonable period afterward in case the listing is reinstated or as required for legal, tax, or security purposes.
              </p>

              <h3>4A. Data Deletion</h3>
              <p>
                User Content may be deleted at any time after account termination unless retention is required by law. Directory listings may be removed at the request of the listed individual or at our discretion, consistent with our Terms of Service. You may request deletion of personal information by contacting{" "}
                <a href="mailto:support@lawfusion.ai">support@lawfusion.ai</a>.
              </p>
            </section>

            <section className="policy-section">
              <h2>5. Sharing</h2>
              <ul>
                <li>
                  <strong>Service Providers:</strong> Hosting, AI/model providers, analytics, and payment processors.
                </li>
                <li>
                  <strong>Directory Visitors:</strong> Directory listing information you submit as an attorney or paralegal is shared publicly with visitors to the Service, including consumers searching the directory.
                </li>
                <li>
                  <strong>Advertising Partners:</strong> Where applicable, and subject to the opt-out described in Section 3B.
                </li>
                <li>
                  <strong>Legal/Compliance:</strong> Where required by law or to protect rights, safety, or the integrity of the Service.
                </li>
              </ul>
              <p>
                We <strong>do not sell personal information for monetary consideration</strong> and honor relevant opt-out rights where applicable, including for any activity that may constitute a "sale" or "share" under applicable state privacy laws.
              </p>
            </section>

            <section className="policy-section">
              <h2>6. Security</h2>
              <p>
                We use reasonable administrative, technical, and physical safeguards. No method is 100% secure. If a data incident occurs, we will notify you where required by law and take reasonable steps to mitigate.
              </p>
            </section>

            <section className="policy-section">
              <h2>7. Your Rights</h2>
              <p>Depending on your jurisdiction, you may have the right to:</p>
              <ul>
                <li><strong>know or access</strong> the personal information we hold about you;</li>
                <li><strong>correct</strong> inaccurate personal information;</li>
                <li><strong>delete</strong> personal information, subject to certain exceptions;</li>
                <li><strong>data portability</strong>, where applicable;</li>
                <li><strong>opt out</strong> of the sale or sharing of personal information;</li>
                <li><strong>limit</strong> the use or disclosure of sensitive personal information, where applicable; and</li>
                <li><strong>non-discrimination</strong> for exercising any of these rights.</li>
              </ul>
              <p>
                We will respond to verifiable requests within the time required by applicable law (for example, the CCPA), and will notify you if additional time is needed. You may opt out of data sharing for marketing, advertising, or analytics by contacting us. We do not sell personal information for monetary consideration but honor applicable opt-out rights, including any "Do Not Sell or Share" requests.
              </p>
              <p>
                These rights are available to all users of the Service, including consumers who use our legal-question tools without creating an account, to the extent we can verify the request in relation to information we hold.
              </p>
            </section>

            <section className="policy-section">
              <h2>8. Children</h2>
              <p>
                The Service is intended for users <strong>18 years of age or older.</strong> We do not knowingly collect personal data from anyone under the age of 18. This applies to all users of the Service, including consumers using our free legal-question tools without an account.
              </p>
            </section>

            <section className="policy-section">
              <h2>9. International Data Transfers</h2>
              <p>
                Our servers are located in the United States. If you access the Service from outside the U.S., your information may be transferred to and processed in jurisdictions that may not provide equivalent data protection.
              </p>
            </section>

            <section className="policy-section">
              <h2>10. Changes</h2>
              <p>
                We may update this Policy by posting a revised version with a new effective date. Continued use after the effective date constitutes acceptance.
              </p>
            </section>

            <section className="policy-section">
              <h2>11. Contact Us</h2>
              <p>
                <strong>Comparative AI Solutions LLC</strong> (Owner of LawFusion.AI)<br />
                Vacaville, California, USA
              </p>
              <ul>
                <li>
                  <strong>Email:</strong>{" "}
                  <a href="mailto:support@lawfusion.ai">support@lawfusion.ai</a>
                </li>
              </ul>
            </section>
          </div>
        </section>
      </main>
    </div>
  );
}
